import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FeatureNoticeService } from '@app/services/feature-notice.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { Button } from 'primeng/button';

/**
 * FeatureNoticeSkodaComponent
 *
 * The user can opt for not showing the notice again.
 *
 */
@Component({
  selector: 'app-feature-notice',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, FormsModule, Button],
  template: `
    <div
      class="relative w-[950px] max-w-[calc(100dvw-2rem)] overflow-hidden rounded-xl bg-[#06131e] p-0 lg:px-10 lg:pb-12 lg:pt-16"
      data-testid="feature-notice">
      <!-- Background layer - covers everything, aligned to right -->
      <div class="absolute inset-y-0 right-0 z-0 w-[500px] bg-[url('/assets/images/audi-notice.png')] bg-cover bg-center"></div>

      <div class="absolute inset-0 bg-gradient-to-r from-[#061a26] from-55% to-transparent"></div>

      <!-- Content layer -->
      <div class="relative z-10 text-white">
        <div class="overflow-hidden rounded-xl">
          <div class="">
            <div class="px-6 pb-4 pt-10 lg:px-8 lg:pb-4 lg:pt-4">
              <h2 class="text-4xl font-semibold">{{ 'featureNotice.audi.headline' | transloco }}</h2>
            </div>

            <div class="mt-4 max-w-[500px] px-6 lg:px-8">
              <p
                [innerHTML]="
                  'featureNotice.audi.text1'
                    | transloco
                      : {
                          bold1: '<strong class=text-primary-500>' + ('featureNotice.audi.text1-bold1' | transloco) + '</strong>',
                          bold2: '<strong class=text-primary-500>' + ('featureNotice.audi.text1-bold2' | transloco) + '</strong>',
                        }
                "></p>

              <div class="mt-6">
                <p class="font-semibold">{{ 'featureNotice.audi.features.title' | transloco }}</p>
                <ul class="mt-2 list-disc pl-5">
                  <li>{{ 'featureNotice.audi.features.list.1' | transloco }}</li>
                  <li>{{ 'featureNotice.audi.features.list.2' | transloco }}</li>
                </ul>
              </div>

              <p class="mt-6">{{ 'featureNotice.audi.text3' | transloco }}</p>

              <p class="mt-1">
                {{ 'featureNotice.audi.text4' | transloco }}
                <a class="inline p-0 text-primary-400 hover:underline" href="mailto:info@qira.net" target="_blank" rel="noopener noreferrer">
                  {{ 'global.email' | transloco }}
                </a>
              </p>
            </div>
          </div>

          <div class="flex items-center justify-end gap-2 px-6 py-8 lg:gap-4 lg:px-6 lg:px-8">
            <label class="block flex items-start justify-end text-gray-500">
              <input class="my-[6px]" type="checkbox" [(ngModel)]="optOut" />
              <span class="ml-2">{{ 'featureNotice.audi.optOutLabel' | transloco }}</span>
            </label>
            <p-button
              class="whitespace-nowrap"
              severity="primary"
              (click)="submit()"
              [label]="'featureNotice.audi.buttonText' | transloco"
              data-testid="feature-notice-submit-btn" />
          </div>
        </div>
      </div>
    </div>
  `,
})
export class FeatureNoticeAudiComponent implements OnInit {
  @Input() data: any;
  @Input() close: (() => void) | undefined;
  @Input() okClicked: (() => void) | undefined;

  featureNoticeService = inject(FeatureNoticeService);

  ngOnInit() {
    this.featureNoticeService.noticeHasBeenShown();
  }

  optOut = false;

  submit() {
    if (this.optOut) {
      this.featureNoticeService.dontShowNoticeAgain();
    }

    this.okClicked?.();
    this.close?.();
  }
}
